"use client";

import { Match, Team } from "@/sources/kicker-amateur/types";
import React, { useRef } from "react";
import {
  ringsideCondensedBookBase64,
  ringsideCondensedSemiBase64,
  ringsideCompressedBookBase64,
  ringsideCompressedBoldBase64,
  knockoutHTF71FullMiddlewtBase64,
  knockoutHTF50WelterweightBase64
} from "@/fonts/base64fonts";
import defaultTeamLogo from "@/assets/icon/default-team-logo.png";
import backgroundImage from "@/assets/generator/AdobeStock_618346374.jpeg";
import VereinsheimLogo from "@/assets/branding/logo.svg";
import Brush01 from '@/assets/generator/brush-01.svg';
import { usePwa } from "@/app/pwa/lib/pwa-context";
import Button from "./Button";
import { getResult } from "@/helpers/gameHelpers";
import { TeamName } from "./team/TeamName";

interface MatchPosterSVGProps {
  match: Match;
  team: Team;
}

export const MatchPosterSVG: React.FC<MatchPosterSVGProps> = ({
  match,
  team
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  const pwa = usePwa();
  const teamColor = pwa.PrimaryColor || "#CC0000";
  const teamSecondaryColor = pwa.SecondaryColor || "#CC0000";

  const hasScore = (getResult(match.ATeamScore) !== null && getResult(match.BTeamScore) !== null && 
  !isNaN(Number(getResult(match.ATeamScore))) && !isNaN(Number(getResult(match.BTeamScore))))
  const hasHalfTimeScore = !!(
    match.ATeamHalfScore != undefined || match.BTeamHalfScore != undefined
  );


  // Preview size
  const previewWidth = 640;
  const previewHeight = previewWidth * 800/640;
  const fullWidth = 1600;
  const fullHeight = 2000;

  const handleDownloadPNG = async () => {
    const svg = svgRef.current;
    if (!svg) return;

    // Clone the SVG and set full size for PNG export
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute("width", fullWidth.toString());
    clone.setAttribute("height", fullHeight.toString());

    // Convert all image URLs to base64
    const images = clone.querySelectorAll('image');
    const imagePromises = Array.from(images).map(async (img) => {
      const href = img.getAttribute('href');
      if (!href || href.startsWith('data:')) return;

      try {
        const response = await fetch(href);
        const blob = await response.blob();
        const reader = new FileReader();
        return new Promise((resolve) => {
          reader.onloadend = () => {
            img.setAttribute('href', reader.result as string);
            resolve(null);
          };
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error loading image:', error);
      }
    });

    // Wait for all images to be converted
    await Promise.all(imagePromises);

    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(clone);
    const svg64 = btoa(unescape(encodeURIComponent(source)));
    const image64 = `data:image/svg+xml;base64,${svg64}`;
    
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.src = image64;
    
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = fullWidth;
      canvas.height = fullHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      
      ctx.fillStyle = "#f5f5f5";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, fullWidth, fullHeight);
      
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        
        // Format date and time
        const date = match.Kickoff ? new Date(match.Kickoff) : new Date();
        const formattedDate = date.toISOString().split('T')[0];
        // const formattedTime = date.toTimeString().split(' ')[0].replace(/:/g, '-');
        
        // Format team names (remove special characters and spaces)
        const teamA = match.ATeam?.Name?.replace(/[^a-zA-Z0-9]/g, '') || 'TeamA';
        const teamB = match.BTeam?.Name?.replace(/[^a-zA-Z0-9]/g, '') || 'TeamB';
        
        link.download = `${formattedDate}-${teamA}-${teamB}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, "image/jpeg");
    };
  };

  const location = [match.Location?.OfficalName, match.Location?.Town?.OfficalName].filter(Boolean).join(", ");

  const kickOff = !hasScore && match.Kickoff ? `${match.Kickoff ? new Date(match.Kickoff).toLocaleDateString('de-DE') : ''}, ${match.Kickoff ? new Date(match.Kickoff).toLocaleTimeString('de-DE', {hour: '2-digit', minute:'2-digit'}) : ''} Uhr` : '';

  const meta = [
    match.Contest?.Name,
    kickOff,
    location
  ].filter(Boolean);

  return (
    <div>
      <div className="aspect-[640/800]">
        <svg
          className="w-full h-full"
          ref={svgRef}
          viewBox="0 0 640 800"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <style>
              {`
                @font-face {
                  font-family: 'Ringside Condensed';
                  src: url(${ringsideCondensedBookBase64}) format('woff2');
                  font-weight: 400;
                  font-style: normal;
                }
                @font-face {
                  font-family: 'Ringside Condensed';
                  src: url(${ringsideCondensedSemiBase64}) format('woff2');
                  font-weight: 600;
                  font-style: normal;
                }
                @font-face {
                  font-family: 'Ringside Compressed';
                  src: url(${ringsideCompressedBookBase64}) format('woff2');
                  font-weight: 400;
                  font-style: normal;
                }
                @font-face {
                  font-family: 'Ringside Compressed';
                  src: url(${ringsideCompressedBoldBase64}) format('woff2');
                  font-weight: 700;
                  font-style: normal;
                }
                @font-face {
                  font-family: 'Knockout HTF71 Full Middlewt';
                  src: url(${knockoutHTF71FullMiddlewtBase64}) format('woff2');
                  font-weight: normal;
                  font-style: normal;
                }
                @font-face {
                  font-family: 'Knockout HTF50 Welterweight';
                  src: url(${knockoutHTF50WelterweightBase64}) format('woff2');
                  font-weight: normal;
                  font-style: normal;
                }
                .condensed {
                  font-family: 'Ringside Condensed', sans-serif;
                }
                .compressed {
                  font-family: 'Ringside Compressed', sans-serif;
                }
                .knockout-htf71-full-middlewt {
                  font-family: 'Knockout HTF71 Full Middlewt', sans-serif;
                }
                .knockout-htf50-welterweight {
                  font-family: 'Knockout HTF50 Welterweight', sans-serif;
                }
              `}
            </style>
            <filter id="team-icon-bg">
              <feGaussianBlur in="SourceGraphic" stdDeviation="70" />
              {/* <feGaussianBlur in="SourceGraphic" stdDeviation="50" /> */}
            </filter>
            <filter id="background">
              <feColorMatrix type="matrix" values="
                0.33 0.33 0.33 0 0
                0.33 0.33 0.33 0 0
                0.33 0.33 0.33 0 0
                0 0 0 1 0
              "/>
            </filter>
            <filter id="background-darken">
              <feComponentTransfer>
                <feFuncR type="linear" slope="0.5"/>
                <feFuncG type="linear" slope="0.5"/>
                <feFuncB type="linear" slope="0.5"/>
              </feComponentTransfer>
            </filter>
            {/* <filter id="textNoise" x="0" y="0" width="100%" height="100%">
              <feTurbulence type="fractalNoise" baseFrequency="0.09" numOctaves="2" seed="7" result="noise"/>
              <feColorMatrix type="matrix" values="1 0 0 0 0
                                                  0 1 0 0 0
                                                  0 0 1 0 0
                                                  0 0 0 0.25 0" result="noiseAlpha"/>
              <feComposite operator="in" in="noiseAlpha" in2="SourceAlpha" result="noiseMasked"/>
              <feBlend in="SourceGraphic" in2="noiseMasked" mode="multiply"/>
            </filter> */}
            <filter id="textNoise">
              <feTurbulence id="turbulenceNode" type="turbulence" baseFrequency="1.404" numOctaves="1" seed="75" stitchTiles="noStitch" result="turb"></feTurbulence>
              <feColorMatrix in="turb" type="matrix" values="
                1 0 0 0 0
                0 1 0 0 0
                0 0 1 0 0
                0 0 0 0.5 0
              " result="mono"></feColorMatrix>
              <feComponentTransfer in="mono" result="contrastAdjusted">
                <feFuncR type="gamma" exponent="10" amplitude="1"></feFuncR>
                <feFuncG type="gamma" exponent="10" amplitude="1"></feFuncG>
                <feFuncB type="gamma" exponent="10" amplitude="1"></feFuncB>
              </feComponentTransfer>
              <feComposite operator="in" in="noiseAlpha" in2="SourceAlpha" result="noiseMasked"/>
              <feBlend in="SourceGraphic" in2="noiseMasked" mode="multiply"/>
            </filter>
            <filter id="textShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.3"/>
            </filter>
          </defs>
          <rect x="0" y="0" width="600" height="800" fill="#f5f5f5" />
          <image
            x="0"
            y="0"
            width="100%"
            height="122%"
            href={backgroundImage.src}
            preserveAspectRatio="xMidYMid slice"
            filter="url(#background) url(#background-darken)"
          />
          <g transform="translate(0, 400)">
            { hasScore 
              ?
                <>
                  <text x="50%" y="90" textAnchor="middle" fontSize="110" fontWeight="600" fill="#FFF" className="knockout-htf50-welterweight" filter="url(#textShadow) url(#textNoise)">
                      {getResult(match.ATeamScore)}<tspan dx="15">:</tspan><tspan dx="15">{getResult(match.BTeamScore)}</tspan>
                  </text>
                  {hasHalfTimeScore && (
                    <text x="50%" y="140" textAnchor="middle" fontSize="20" fill="#FFF" className="compressed" filter="url(#textNoise) url(#textShadow)">
                      ({getResult(match.ATeamHalfScore)} : {getResult(match.BTeamHalfScore)})
                    </text>
                  )}
                </>
              : 
                <text x="50%" y="70" textAnchor="middle" fontSize="68" fontWeight="600" fill="#FFF" className="knockout-htf50-welterweight" filter="url(#textShadow) url(#textNoise)">
                    VS
                </text>
            }
            {/* Team A Group */}
            <g transform="translate(135, 0)">
              <image
                width="660"
                x="-510"
                y="-150"
                href={match.ATeam?.MediaServiceImages?.find(Boolean)?.AbsoluteUrl ?? ((match.ATeam && match.ATeam.Id) ? `/api/image/team/${match.ATeam.Id!.toString()}` : defaultTeamLogo.src)}
                preserveAspectRatio="xMidYMid meet"
                filter="url(#team-icon-bg)"
                opacity="0.1"
              />
              <image
                width="110"
                x="-55"
                href={match.ATeam?.MediaServiceImages?.find(Boolean)?.AbsoluteUrl ?? ((match.ATeam && match.ATeam.Id) ? `/api/image/team/${match.ATeam.Id!.toString()}` : defaultTeamLogo.src)}
                preserveAspectRatio="xMidYMid meet"
              />
            
              <text width="100" y="140" x="0" textAnchor="middle" fontSize="20" fontWeight="700" fill="#FFF" className="compressed" filter="url(#textNoise) url(#textShadow)">
                {match.ATeam?.Name}
              </text>
              <text y="165" x="0" textAnchor="middle" fontSize="20" fill="#FFF" className="compressed" filter="url(#textNoise) url(#textShadow)">
                <TeamName team={match.BTeam} teamTypeName/>
              </text>
            </g>
            {/* Team B Group */}
            <g transform="translate(505, 0)">
              <image
                width="660"
                x="-150"
                y="-150"
                href={match.BTeam?.MediaServiceImages?.find(Boolean)?.AbsoluteUrl ?? ((match.BTeam && match.BTeam.Id) ? `/api/image/team/${match.BTeam.Id!.toString()}` : defaultTeamLogo.src)}
                preserveAspectRatio="xMidYMid meet"
                filter="url(#team-icon-bg)"
                opacity="0.1"
                />
              <image
                x="-55"
                width="110"
                href={match.BTeam?.MediaServiceImages?.find(Boolean)?.AbsoluteUrl ?? ((match.BTeam && match.BTeam.Id) ? `/api/image/team/${match.BTeam.Id!.toString()}` : defaultTeamLogo.src)}
                preserveAspectRatio="xMidYMid meet"
              />
              <text y="140" x="0" textAnchor="middle" fontSize="20" fontWeight="700" fill="#FFF" className="compressed" filter="url(#textNoise) url(#textShadow)">
                {match.BTeam?.Name}
              </text>
              <text y="165" x="0" textAnchor="middle" fontSize="20" fill="#FFF" className="compressed" filter="url(#textNoise) url(#textShadow)">
                <TeamName team={match.BTeam} teamTypeName/>
              </text>
            </g>
          </g>
          {/* <image
            x="calc(50% - 28px)"
            y="80"
            width="56"
            href={team?.MediaServiceImages?.find(Boolean)?.AbsoluteUrl ?? ((team && team.Id) ? `/api/image/team/${team.Id!.toString()}` : defaultTeamLogo.src)}
            preserveAspectRatio="xMidYMid meet"
          /> */}
          <g transform={`translate(0, ${330 - (meta.length * 32)})`}>
            <text x="50%" y="0" textAnchor="middle" fontSize="76" className="knockout-htf71-full-middlewt" fill="#FFF" style={{textTransform: "uppercase"}} filter="url(#textNoise) url(#textShadow)">
              {match.Round?.Name}
            </text>
            {meta.map((item, index) => (
              <text key={index} x="50%" y={`${index * 32 + 45}`} textAnchor="middle" fontSize="24" fill="#FFF" className="compressed" filter="url(#textNoise) url(#textShadow)">
                {item}
              </text>
            ))}
          </g>
          
          {/* Footer */}
          <g transform="translate(-200, -520) scale(1) rotate(5)">
            <Brush01 fill={teamColor} />
          </g>
          <g transform="translate(300,480) scale(1) rotate(12)">
            <Brush01 fill={teamSecondaryColor} />
          </g>
          <g transform="translate(240, 750)">
            <rect
              x={"-10"}
              y={"-10"}
              width={"180"}
              height={"60"}
              fill="#cc0000"
            />
            <VereinsheimLogo width={160} height={40} />
          </g>
        </svg>
      </div>
      <div className="mt-4">
        <Button
          onClick={handleDownloadPNG}
          color="primary"
          className="w-full justify-center"
        >
          Herunterladen
        </Button>
      </div>
    </div>
  );
};

export default MatchPosterSVG; 
